import api from './api'
import type { Instance, PaginatedResponse } from '@/types'
import type { ApiResponse, CreateInstanceRequest } from '@/types/api'

export interface InstanceConnectionPayload {
  qr_code_data?: string
  status: Instance['status']
  public_qr_url?: string | null
  remote_instance_id?: string | null
  connected_at?: string | null
  phone_number?: string | null
  jid?: string | null
  profile_picture_url?: string | null
}

const InstancesService = {
  async getAll(): Promise<Instance[]> {
    const response = await api.get<PaginatedResponse<Instance>>(
      '/api/v1/instances',
    )
    return response.data.data
  },

  async getById(instanceId: string): Promise<Instance> {
    const response = await api.get<ApiResponse<Instance>>(`/api/v1/instances/${instanceId}`)
    return response.data.data
  },

  async create(data: CreateInstanceRequest): Promise<Instance> {
    const response = await api.post<ApiResponse<Instance>>('/api/v1/instances', data)
    return response.data.data
  },

  async delete(instanceId: string): Promise<void> {
    await api.delete(`/api/v1/instances/${instanceId}`)
  },

  async disconnect(instanceId: string): Promise<void> {
    await api.post(`/api/v1/instances/${instanceId}/disconnect`)
  },

  async getQRCode(instanceId: string): Promise<InstanceConnectionPayload> {
    const response = await api.get<
      ApiResponse<{
        qr_image?: string | null
        instance_id: string
        status: Instance['status']
        public_qr_url?: string | null
        remote_instance_id?: string | null
        profile_picture_url?: string | null
      }>
    >(
      `/api/v1/instances/${instanceId}/qr`,
    )

    const qrImage = response.data.data.qr_image
    return {
      qr_code_data: qrImage
        ? qrImage.startsWith('data:')
          ? qrImage
          : `data:image/png;base64,${qrImage}`
        : undefined,
      status: response.data.data.status,
      public_qr_url: response.data.data.public_qr_url,
      remote_instance_id: response.data.data.remote_instance_id,
      profile_picture_url: response.data.data.profile_picture_url,
    }
  },

  async getStatus(instanceId: string): Promise<InstanceConnectionPayload> {
    const response = await api.get<
      ApiResponse<{
        status: Instance['status']
        connected_at?: string | null
        phone_number?: string | null
        jid?: string | null
        profile_picture_url?: string | null
        qr_code_data?: string | null
        public_qr_url?: string | null
        remote_instance_id?: string | null
      }>
    >(`/api/v1/instances/${instanceId}/status`)

    const qrCodeData = response.data.data.qr_code_data
    return {
      status: response.data.data.status,
      connected_at: response.data.data.connected_at,
      phone_number: response.data.data.phone_number,
      jid: response.data.data.jid,
      profile_picture_url: response.data.data.profile_picture_url,
      qr_code_data: qrCodeData
        ? qrCodeData.startsWith('data:')
          ? qrCodeData
          : `data:image/png;base64,${qrCodeData}`
        : undefined,
      public_qr_url: response.data.data.public_qr_url,
      remote_instance_id: response.data.data.remote_instance_id,
    }
  },

  async updateSettings(
    instanceId: string,
    settings: Partial<Instance>,
  ): Promise<Instance> {
    const response = await api.put<ApiResponse<Instance>>(
      `/api/v1/instances/${instanceId}/settings`,
      settings,
    )
    return response.data.data
  },
}

export default InstancesService
